import { Col, Row, Label } from "reactstrap";
import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import {
  staffLeaveRestrictionPeriodCreate,
  staffLeaveRestrictionPeriodCreateSuccess,
  staffLeaveRestrictionPeriodList,
  staffLeaveRestrictionPeriodUpdate,
  staffLeaveRestrictionPeriodUpdateSuccess,
} from "../../../store/staffLeave/action";
import AppModal from "../../Common/AppModal";
import { handleValidDateTime } from "../../Common/HandleDateTime";
import { AppDateTimePicker } from "../../Common/AppDateTimePicker";
import moment from "moment";

export default function CreateEditStaffLeaveRestrictionPeriodConfig({
  modal,
  setModal,
  toggle,
  isEdit,
  updateData,
}) {
  const dispatch = useDispatch();

  const initialData = {
    Name: "",
    Notes: "",
    StartDateTime: "",
    EndDateTime: "",
    // Task 24488. HARD is the default because that is what every existing
    // restriction period is back-seeded to, and because a restriction that
    // quietly does not restrict is the worse mistake to make by accident.
    RestrictionType: "HARD",
  };

  const [data, setData] = useState(initialData);

  useEffect(() => {
    if (!isEdit) return;
    if (isEmpty(updateData)) return;

    setData({
      Id: updateData?.Id,
      Name: updateData?.Name,
      Notes: updateData?.Notes,
      StartDateTime: updateData?.StartDateTime,
      EndDateTime: updateData?.EndDateTime,
      RestrictionType:
        String(updateData?.RestrictionType || "").toUpperCase() === "SOFT"
          ? "SOFT"
          : "HARD",
    });
  }, [updateData, isEdit]);

  const handleCreateUpdateData = (type) => {
    // Optional: Add validation here before dispatching
    // if (isEmpty(data.Name) || isEmpty(data.StartDateTime)) {
    //   toast.error("Name and Start Date are required");
    //   return;
    // }

    if (type === "create") {
      dispatch(staffLeaveRestrictionPeriodCreate([data]));
    }
    if (type === "update") {
      dispatch(staffLeaveRestrictionPeriodUpdate([data]));
    }
  };

  const {
    CreateSUCCESS,
    UpdateSUCCESS,
    staffLeaveRestrictionPeriodCreateLoading,
    staffLeaveRestrictionPeriodUpdateLoading,
  } = useSelector((state) => ({
    CreateSUCCESS: state.StaffLeave.staffLeaveRestrictionPeriodCreate,
    UpdateSUCCESS: state.StaffLeave.staffLeaveRestrictionPeriodUpdate,
    staffLeaveRestrictionPeriodCreateLoading:
      state.StaffLeave.staffLeaveRestrictionPeriodCreateLoading,
    staffLeaveRestrictionPeriodUpdateLoading:
      state.StaffLeave.staffLeaveRestrictionPeriodUpdateLoading,
  }));

  // Handle Create Success/Fail
  useEffect(() => {
    if (isEmpty(CreateSUCCESS)) return;
    if (CreateSUCCESS.status === 200) {
      toast.success(CreateSUCCESS.message);
      setModal(false);
      dispatch(staffLeaveRestrictionPeriodList());
    } else {
      toast.error(CreateSUCCESS.message);
    }
    dispatch(staffLeaveRestrictionPeriodCreateSuccess([]));
  }, [CreateSUCCESS, dispatch, setModal]);

  // Handle Update Success/Fail
  useEffect(() => {
    if (isEmpty(UpdateSUCCESS)) return;
    if (UpdateSUCCESS.status === 200) {
      toast.success(UpdateSUCCESS.message);
      setModal(false);
      dispatch(staffLeaveRestrictionPeriodList());
    } else {
      toast.error(UpdateSUCCESS.message);
    }
    dispatch(staffLeaveRestrictionPeriodUpdateSuccess([]));
  }, [UpdateSUCCESS, dispatch, setModal]);

  return (
    <AppModal
      open={modal}
      onClose={toggle}
      title={
        isEdit
          ? "Edit Staff Leave Restriction Period"
          : "Create Staff Leave Restriction Period"
      }
      icon={<BlockOutlinedIcon />}
      headerAccent="indigo"
      size="sm"
      loading={
        staffLeaveRestrictionPeriodCreateLoading ||
        staffLeaveRestrictionPeriodUpdateLoading
      }
      loadingText="Saving…"
      submitLabel={isEdit ? "Update" : "Create"}
      submitColor="success"
      onSubmit={() =>
        isEdit
          ? handleCreateUpdateData("update")
          : handleCreateUpdateData("create")
      }
    >
      <Row className="g-3">
            <Col lg={12}>
              <div className={"d-flex flex-column p-2"}>
                <Label>How strict is this?</Label>

                <div className={"d-flex flex-column gap-2 mt-1"}>
                  <label className={"d-flex gap-2 align-items-start"}>
                    <input
                      type="radio"
                      className={"form-check-input mt-1 flex-shrink-0"}
                      name="restriction-type"
                      checked={data.RestrictionType !== "SOFT"}
                      onChange={() =>
                        setData({ ...data, RestrictionType: "HARD" })
                      }
                    />
                    <span>
                      <span className={"fw-semibold d-block"}>Hard stop</span>
                      <span className={"text-muted small"}>
                        Staff cannot request leave over these dates at all. They
                        are told why when they try.
                      </span>
                    </span>
                  </label>

                  <label className={"d-flex gap-2 align-items-start"}>
                    <input
                      type="radio"
                      className={"form-check-input mt-1 flex-shrink-0"}
                      name="restriction-type"
                      checked={data.RestrictionType === "SOFT"}
                      onChange={() =>
                        setData({ ...data, RestrictionType: "SOFT" })
                      }
                    />
                    <span>
                      <span className={"fw-semibold d-block"}>Soft stop</span>
                      <span className={"text-muted small"}>
                        Staff see a warning but can still submit, and whoever
                        approves it sees the same warning.
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              <div className={"d-flex flex-column p-2"}>
                <Label>Notes</Label>
                <textarea
                  className={"form-control"}
                  rows="3"
                  value={data.Notes}
                  onChange={(e) => setData({ ...data, Notes: e.target.value })}
                  placeholder="e.g. Year end stocktake - no leave in this week"
                />
                <div className={"text-muted small mt-1"}>
                  This is shown to the staff member when they hit this
                  restriction, so say why.
                </div>
              </div>

              <div className={"d-flex flex-column p-2"}>
                <Label>Start Date</Label>
                <AppDateTimePicker
                  value={
                    data?.StartDateTime
                      ? moment(data?.StartDateTime, "YYYY-MM-DD HH:mm:ss")
                      : null
                  }
                  onChange={(val) => {
                    const jsDate = val && val.isValid() ? val.toDate() : null;
                    setData({
                      ...data,
                      StartDateTime: jsDate ? handleValidDateTime(jsDate) : "",
                    });
                  }}
                />
              </div>

              <div className={"d-flex flex-column p-2"}>
                <Label>End Date</Label>
                <AppDateTimePicker
                  value={
                    data?.EndDateTime
                      ? moment(data?.EndDateTime, "YYYY-MM-DD HH:mm:ss")
                      : null
                  }
                  onChange={(val) => {
                    const jsDate = val && val.isValid() ? val.toDate() : null;
                    setData({
                      ...data,
                      EndDateTime: jsDate ? handleValidDateTime(jsDate) : "",
                    });
                  }}
                />
              </div>

            </Col>
          </Row>
    </AppModal>
  );
}
